// 引入 React 运行时。
import React from "react";
// 引入 className 合并工具。
import cx from "classnames";
// 引入帖子列表组件。
import UserPostList from "@/pages/user-center/components/UserPostList";
import UserReplyList from "@/pages/user-center/components/UserReplyList";
// 引入消息列表组件。
import MessageList from "@/pages/user-center/components/MessageList";
import { IThreadLisItem } from "@/api/thread/list";
import { USER_ACTION_TYPE } from "@/components/UserAction";
import type { IUserReplyItem } from "@/api/userReplies";
// 引入帖子空态图标。
import EmptyPostsIcon from "../../../assets/icon/account/empty-posts.svg";
// 引入喜欢空态图标。
import EmptyLikesIcon from "../../../assets/icon/account/empty-likes.svg";
// 引入收藏空态图标。
import EmptyCollectionsIcon from "../../../assets/icon/account/empty-collections.svg";
// 引入消息空态图标。
import EmptyMessagesIcon from "../../../assets/icon/account/empty-messages.svg";
// 引入评论空态图标。
import EmptyCommentsIcon from "../../../assets/icon/account/empty-comments.svg";
// 引入样式。
import styles from "@/pages/user-center/index.module.scss";

// PC tab 内容渲染入参类型。
interface RenderTabContentParams {
  tabKey: string;
  isSelfView: boolean;
  t: (key: string) => string;
  showDrafts: boolean;
  showPosts: boolean;
  drafts: any[];
  publishedPosts: any[];
  postsLoading: boolean;
  postsHasMore: boolean;
  handleItemUpdate: any;
  likesInitialized: boolean;
  likesData: any[];
  likesLoading: boolean;
  likesHasMore: boolean;
  collectionsInitialized: boolean;
  collectionsData: any[];
  collectionsLoading: boolean;
  collectionsHasMore: boolean;
  repliesInitialized: boolean;
  repliesData: IUserReplyItem[];
  repliesLoading: boolean;

  messagesInitialized: boolean;
  messagesData: any[];
  messagesLoading: boolean;
  setShowPointsExchangeModal: (val: boolean) => void;
}

// 根据当前 tab 渲染对应内容区域。
export const renderPcTabContent = ({
  tabKey,
  isSelfView,
  t,
  showDrafts,
  showPosts,
  drafts,
  publishedPosts,
  postsLoading,
  postsHasMore,
  handleItemUpdate,
  likesInitialized,
  likesData,
  likesLoading,
  likesHasMore,
  collectionsInitialized,
  collectionsData,
  collectionsLoading,
  collectionsHasMore,
  repliesInitialized,
  repliesData,
  repliesLoading,
  messagesInitialized,
  messagesData,
  messagesLoading,
  setShowPointsExchangeModal,
}: RenderTabContentParams) => {
  // 当前页面名用于埋点字段。
  const currentPageName = isSelfView ? "MyProfilePage" : "UserProfilePage";
  // 按 tabKey 走分支渲染。
  switch (tabKey) {
    case "myPosts":
      if (!showDrafts && !showPosts) {
        return (
          <div className={cx(styles.emptyState, styles.likeEmptyState)}>
            <div className={styles.emptyIconPosts}>
              <EmptyPostsIcon />
            </div>
            <p className={styles.emptyTextPosts}>
              {isSelfView ? (
                <>
                  {t("user_center_empty_works_line1")}
                </>
              ) : (
                t("user_center_pc_ta_no_works")
              )}
            </p>
          </div>
        );
      }
      return (
        <div className={styles.tabContent}>
          {showDrafts && (
            <div className={styles.section}>
              <UserPostList data={drafts} isDraft={true} loading={publishedPosts?.length > 0 ? false : postsLoading} onItemUpdate={handleItemUpdate} pageName={currentPageName} />
            </div>
          )}
          {showPosts && (
            <div className={styles.section}>
              <UserPostList
                data={publishedPosts}
                loading={postsLoading}
                hasMore={postsHasMore}
                noMoreText={t("user_center_pc_all_posts_shown")}
                onItemUpdate={handleItemUpdate}
                pageName={currentPageName}
                listName={isSelfView ? t("user_center_pc_my_posts") : undefined}
              />
            </div>
          )}
        </div>
      );
    case "myLikes":
      if (likesInitialized && likesData.length === 0 && !likesLoading) {
        return (
          <div className={cx(styles.emptyState, styles.likeEmptyState)}>
            <div className={styles.emptyIconPosts}>
              <EmptyLikesIcon />
            </div>
            <p className={styles.emptyTextPosts}>
              {t("user_center_pc_empty_likes_line1")}
            </p>
          </div>
        );
      }
      return (
        <div className={styles.tabContent}>
          <div className={styles.section}>
            <UserPostList
              data={likesData}
              loading={likesLoading}
              hasMore={likesHasMore}
              noMoreText={t("user_center_pc_all_posts_shown")}
              onItemUpdate={handleItemUpdate}
              pageName={currentPageName}
              listName={isSelfView ? t("user_center_pc_my_likes") : undefined}
            />
          </div>
        </div>
      );
    case "myCollections":
      if (collectionsInitialized && collectionsData.length === 0 && !collectionsLoading) {
        return (
          <div className={cx(styles.emptyState, styles.likeEmptyState)}>
            <div className={styles.emptyIconPosts}>
              <EmptyCollectionsIcon />
            </div>
            <p className={styles.emptyTextPosts}>
              {t("user_center_pc_empty_collections_line1")}
            </p>
          </div>
        );
      }
      return (
        <div className={styles.tabContent}>
          <div className={styles.section}>
            <UserPostList
              data={collectionsData}
              loading={collectionsLoading}
              hasMore={collectionsHasMore}
              noMoreText={t("user_center_pc_all_posts_shown")}
              onItemUpdate={handleItemUpdate}
              pageName={currentPageName}
              listName={isSelfView ? t("user_center_pc_my_collections") : undefined}
            />
          </div>
        </div>
      );
    case "messages":
      if (messagesInitialized && messagesData.length === 0 && !messagesLoading) {
        return (
          <div className={cx(styles.emptyState, styles.likeEmptyState)}>
            <div className={styles.emptyIconPosts}>
              <EmptyMessagesIcon />
            </div>
            <p className={styles.emptyTextPosts}>{t("user_center_pc_no_messages")}</p>
          </div>
        );
      }
      return (
        <div className={styles.tabContent}>
          <div className={styles.section}>
            <MessageList
              data={messagesData}
              loading={messagesLoading}
              onOpenPointsExchange={() => setShowPointsExchangeModal(true)}
            />
          </div>
        </div>
      );
    case "myReplies":
      if (repliesInitialized && repliesData.length === 0 && !repliesLoading) {
        return (
          <div className={cx(styles.emptyState, styles.likeEmptyState)}>
            <div className={styles.emptyIconPosts}>
              <EmptyCommentsIcon />
            </div>
            <p className={styles.emptyTextPosts}>{t("user_center_pc_no_my_comments")}</p>
          </div>
        );
      }
      return (
        <div className={styles.tabContent}>
          <div className={styles.section} style={{ minHeight: 'calc(100vh - 80px)', backgroundColor: '#fff',borderRadius: '12px 12px 0 0' }}>
            <UserReplyList data={repliesData} loading={repliesLoading} />
          </div>
        </div>
      );
    default:
      // 未知 tab 返回空。
      return null;
  }
};

// 判断当前 tab 是否命中“已初始化且空数据”的空态条件。
export const isPcTabContentEmpty = ({
  activeTab,
  showDrafts,
  showPosts,
  likesInitialized,
  likesData,
  likesLoading,
  collectionsInitialized,
  collectionsData,
  collectionsLoading,
  repliesInitialized,
  repliesData,
  repliesLoading,
  messagesInitialized,
  messagesData,
  messagesLoading,
}: any) => {
  switch (activeTab) {
    case "myPosts":
      return !showDrafts && !showPosts;
    case "myLikes":
      return likesInitialized && likesData.length === 0 && !likesLoading;
    case "myCollections":
      return collectionsInitialized && collectionsData.length === 0 && !collectionsLoading;
    case "messages":
      return messagesInitialized && messagesData.length === 0 && !messagesLoading;
    case "myReplies":
      return repliesInitialized && repliesData.length === 0 && !repliesLoading;
    default:
      return true;
  }
};
